import { Button } from "@dmca/frontend/components/button";
import { Input, Select } from "@dmca/frontend/components/input";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import { GlobalNavbarCenter, PageHeader } from "@dmca/frontend/components/nav";
import {
  checkPermissionsInLoader,
  useAppPermissions,
} from "@dmca/frontend/hooks";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import type { IProductionStorage } from "@dmca/models/interface/production-storage";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

const ALL_PLANTS = "__all__";

export const Route = createFileRoute("/_authed/production-storage-lookup")({
  component: ProductionStorageLookupPage,
  loader: async ({ context }) =>
    await checkPermissionsInLoader("ProductionJob::ViewJobStorage", context),
  head: () => ({
    meta: [{ title: "Storage Lookup | DNA Dashboard" }],
  }),
});

function ProductionStorageLookupPage() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const hasPermission = useAppPermissions();
  const canRemoveStorage = hasPermission("ProductionJob::RemoveJobStorage");
  const [selectedPlantId, setSelectedPlantId] = useState(ALL_PLANTS);
  const [searchInput, setSearchInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [removeRow, setRemoveRow] = useState<IProductionStorage>();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: locations = [] } = useQuery(
    trpc.cell.getAll.queryOptions({
      byFeatureFlag: CellFeaturesEnum.JOB_STORAGE,
    })
  );

  const plantOptions = useMemo(
    () => [
      { name: "All Plants", value: ALL_PLANTS },
      ...Array.from(
        new Map(
          locations.map((location) => [
            location.plantId,
            {
              name: location.plantName,
              value: location.plantId,
            },
          ])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [locations]
  );

  const plantNameById = useMemo(
    () =>
      new Map(
        locations.map((location) => [location.plantId, location.plantName])
      ),
    [locations]
  );
  const normalizedSearch = searchInput.trim();

  const queryInput = useMemo(
    () => ({
      jobId: normalizedSearch,
      includeRemoved: false,
    }),
    [normalizedSearch]
  );

  const { data = [], isFetching } = useQuery(
    trpc.productionStorage.getAll.queryOptions(queryInput, {
      enabled: /^\d{7}$/.test(normalizedSearch),
      retry: false,
    })
  );

  const storageRows = useMemo(
    () =>
      data.filter(
        (row) =>
          selectedPlantId === ALL_PLANTS ||
          row.storageLocationPlantId === selectedPlantId
      ),
    [data, selectedPlantId]
  );

  const updateMutation = useMutation(
    trpc.productionStorage.remove.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries(trpc.productionStorage.getAll.pathFilter());
        await qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.pathFilter()
        );
        toast.success("Work order marked removed.");
        setSearchInput("");
        setRemoveRow(undefined);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to remove work order.");
      },
    })
  );

  useEffect(() => {
    if (scanning) {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }
  }, [scanning]);

  // Clear input when enabling SCANNING
  const toggleScan = () => {
    setScanning((prev) => {
      if (!prev) setSearchInput("");
      return !prev;
    });
  };

  return (
    <>
      <GlobalNavbarCenter>
        <Tabs
          value="lookup"
          onChange={(_, value: "tracker" | "lookup") => {
            if (value === "tracker") {
              navigate({ to: "/production-storage" });
            }
          }}
          sx={{
            height: "100%",
            "& .MuiTabs-flexContainer": { height: "100%" },
          }}
          centered
        >
          <Tab label="Tracker" value="tracker" />
          <Tab label="Lookup" value="lookup" />
        </Tabs>
      </GlobalNavbarCenter>
      <PageHeader>Storage Lookup</PageHeader>
      <Container maxWidth="md">
        <Stack spacing={2}>
          <Select
            label="Plant"
            allowNull={false}
            value={selectedPlantId}
            options={plantOptions}
            onChange={(e) => setSelectedPlantId(e.target.value)}
          />
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Button
              data-testid="storage-lookup-scan-toggle-btn"
              onClick={toggleScan}
              startIcon={<QrCodeScannerIcon fontSize="small" />}
              sx={{
                bgcolor: scanning
                  ? "success.main"
                  : "action.disabledBackground",
                color: scanning ? "success.contrastText" : "text.primary",
                px: 2,
                py: 1.5,
                borderRadius: "50px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 700,
                fontSize: "0.875rem",
                userSelect: "none",
                transition: "all 0.2s",
                flexShrink: 0,
              }}
            >
              {scanning ? "SCANNING..." : "SCAN"}
            </Button>
            <Input
              label="Work Order ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (!scanning || e.key !== "Enter") return;
                e.preventDefault();
                window.setTimeout(() => {
                  searchInputRef.current?.focus();
                  searchInputRef.current?.select();
                });
              }}
              inputMode="numeric"
              error={!!searchInput && !/^\d{0,7}$/.test(searchInput)}
              errorText="Work Order ID must be a 7-digit number."
              inputRef={searchInputRef}
              sx={{ flex: 1 }}
            />
          </Stack>

          <Paper sx={{ px: 2, position: "relative" }}>
            {isFetching && (
              <CircularProgress
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
            <Box sx={{ opacity: isFetching ? 0.2 : 1 }}>
              {!isFetching &&
                /^\d{7}$/.test(normalizedSearch) &&
                storageRows.length === 0 && (
                  <Typography sx={{ py: 2 }}>
                    No storage record found for the selected plant.
                  </Typography>
                )}
              {storageRows.map((storageRow, index) => (
                <Box key={storageRow.storageLocationId}>
                  {index > 0 && <Divider sx={{ my: 3 }} />}
                  <DisplayRow label="Job" value={storageRow.jobId} />
                  <DisplayRow
                    label="Plant"
                    value={
                      plantNameById.get(storageRow.storageLocationPlantId) ??
                      storageRow.storageLocationPlantId
                    }
                  />
                  <DisplayRow
                    label="Storage Location"
                    value={storageRow.storageLocationName}
                  />
                  <DisplayRow label="Lane" value={storageRow.lane} emphasized />
                  <DisplayRow label="Cart" value={storageRow.cart} emphasized />
                  <DisplayRow
                    label="On Skid"
                    value={storageRow.onSkid ? "Yes" : "No"}
                  />
                  {storageRow.informsProjectStorageStatus && (
                    <DisplayRow
                      label="Finished"
                      value={storageRow.isFinished ? "Finished" : "No"}
                    />
                  )}
                  <DisplayRow label="Notes" value={storageRow.notes} />
                  {canRemoveStorage && (
                    <Button
                      color="error"
                      disabled={updateMutation.isPending}
                      fullWidth
                      sx={{ mt: 3 }}
                      onClick={() => setRemoveRow(storageRow)}
                    >
                      Remove Work Order
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        </Stack>
      </Container>
      <ConfirmationModal
        message="Are you sure you want to mark this work order removed?"
        open={!!removeRow}
        onClose={() => setRemoveRow(undefined)}
        onConfirm={() => {
          if (!removeRow) return;
          updateMutation.mutate({
            jobId: removeRow.jobId,
            storageLocationId: removeRow.storageLocationId,
            isRemoved: true,
          });
        }}
      />
    </>
  );
}

function DisplayRow({
  label,
  value,
  emphasized,
}: {
  label: string;
  value?: string | number | null;
  emphasized?: boolean;
}) {
  return (
    <>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="overline" sx={{ fontSize: 18 }}>
          {label}
        </Typography>
        <Typography
          variant="overline"
          sx={{
            fontSize: emphasized ? 34 : 18,
            fontWeight: emphasized ? 600 : undefined,
          }}
        >
          {value ?? ""}
        </Typography>
      </Stack>
      <Divider />
    </>
  );
}
